import React, { useEffect, Suspense, lazy } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { SearchProvider } from "./context/searchcontext";
import { AuthProvider } from "./context/authcontext";
import { UIProvider } from "./context/uicontext";
import { useSearch } from "./context/searchcontext";
import { useAuth } from "./context/authcontext";
import Navbar from "./components/navbar";
import Footer from "./components/footer";
import ScrollToTop from "./components/ScrollToTop";
import { isListingSlug } from "./utils/slugutils";
import { usePageTracking } from "./hooks/usePageTracking";

// Keep Home eager — it's the landing page, needed immediately on first load
import Home from "./components/home";

// Lazy-load everything else — only downloaded when that route is actually visited
const Listings = lazy(() => import("./components/listings"));
const PGDetail = lazy(() => import("./components/pgdetail"));
const ThankYou = lazy(() => import("./components/thankyou"));
const About = lazy(() => import("./components/aboutNew"));
const Contact = lazy(() => import("./components/contact"));
const OwnerDashboard = lazy(() => import("./components/ownerdashboard"));
const TenantDashboard = lazy(() => import("./components/tenantdashboard"));
const TenantFormPage = lazy(() => import("./components/TenantFormPage"));
const TenantFormPageByBarcodeId = lazy(() =>
  import("./components/TenantFormPageByBarcodeId")
);
const Blog = lazy(() => import("./components/blog/pages/Blog"));
const BlogEditor = lazy(() => import("./components/blog/pages/BlogEditor"));
const Branding = lazy(() => import("./components/branding"));
const RentReceipt = lazy(() => import("./components/rentReceipt"));
const AgreementView = lazy(() =>
  import("./components/RentAgreementViewDynamic")
);

const shouldShowFooter = (pathname) =>
  pathname === "/" ||
  pathname === "/listings" ||
  pathname === "/blog" ||
  pathname === "/about" ||
  pathname === "/thank-you" ||
  pathname === "/contact" ||
  isListingSlug(pathname) ||
  pathname.startsWith("/pg/") ||
  pathname.startsWith("/blog/") ||
  pathname.startsWith("/promo/");

// ── Replaces the broken `/pg-*` route ────────────────────────────────────────
const CatchAll = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/pg-")) {
    return <Listings />;
  }
  return <Navigate to="/" replace />;
};

// ✅ Protected Route wrapper component
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, isLoading } = useAuth(); // ✅ Get isLoading from context
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return; // ✅ Wait for loading to complete

    if (!user) {
      navigate("/", { replace: true });
      return;
    }

    if (allowedTypes && !allowedTypes.includes(user.type)) {
      navigate("/", { replace: true });
    }
  }, [user, navigate, allowedTypes, isLoading]); // ✅ Add isLoading to dependencies

  if (isLoading) return null; // ✅ Show nothing while loading
  if (!user) return null;
  if (allowedTypes && !allowedTypes.includes(user.type)) return null;

  return children;
};

// Simple fallback shown while a lazy chunk downloads.
// Kept minimal/invisible so it doesn't cause layout jumps or flashes.
const RouteFallback = () => <div style={{ minHeight: "50vh" }} />;

const SiteLayout = () => {
  const { pathname } = useLocation();
  const { isSearching } = useSearch();
  usePageTracking();

  return (
    <>
      <ScrollToTop />
      <Navbar />
      {isSearching && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "#fff",
            zIndex: 500,
          }}
        >
          <img
            src="/assets/aapkapgLoader.gif"
            alt="Loading..."
            style={{ width: "auto", height: "10vh" }}
          />
        </div>
      )}
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/listings" element={<Listings />} />
          <Route path="/pg/:pgSlug" element={<PGDetail />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
          {/* Not linked anywhere yet — reachable only by typing the URL.
              Gate this behind ProtectedRoute (like /owner-dashboard below)
              once you decide who's allowed to author posts. */}
          <Route path="/blog-editor" element={<BlogEditor />} />
          {/* <Route 
            path="/blog-editor" 
            element={
              <ProtectedRoute allowedTypes={['admin']}>
                <BlogEditor />
              </ProtectedRoute>
            } 
          /> */}
          <Route path="/promo/:branding" element={<Branding />} />
          <Route path="*" element={<CatchAll />} />
        </Routes>
      </Suspense>
      {shouldShowFooter(pathname) && <Footer />}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <UIProvider>
        <SearchProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* ✅ Protected routes - MUST be defined BEFORE the /* route */}
              <Route
                path="/owner-dashboard/*"
                element={
                  <ProtectedRoute allowedTypes={["pg"]}>
                    <OwnerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tenant-dashboard"
                element={
                  <ProtectedRoute allowedTypes={["tenant"]}>
                    <TenantDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Public routes */}
              <Route path="/tenant-form/:pgId" element={<TenantFormPage />} />
              <Route
                path="/agreement/:encryptedParams"
                element={<AgreementView />}
              />
              <Route path="/rentreceipt" element={<RentReceipt />} />
              <Route
                path="/tenant-form-page/:BarcodeId"
                element={<TenantFormPageByBarcodeId />}
              />

              {/* ✅ Catch-all route - MUST be LAST */}
              <Route path="/*" element={<SiteLayout />} />
            </Routes>
          </Suspense>
        </SearchProvider>
      </UIProvider>
    </AuthProvider>
  );
}

export default App;